import { Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

import { DeployStatusCode } from '@gfe/core/models/plan.model';
import {
  buildDeployTooltipHtml,
  computeOverallDeployStatus
} from '@gfe/core/utils/deploy-status.utils';
import { isNil } from '@gfe/core/utils/shared-utils';

import { TooltipComponent } from '@shared/components/tooltip/tooltip.component';

interface DeployStatusEntry {
  name: string;
  status: DeployStatusCode;
  updatedAt?: Date;
}

/**
 * The richer value a caller passes when it knows how many channels a full deploy covers.
 * A bare array stays accepted: the planning-space list renders a space's last statuses and
 * has no channel count to give.
 */
export interface DeployStatusCellValue {
  statuses: DeployStatusEntry[];
  expectedChannels?: number;
}

@Component({
  selector: 'app-deploy-status-overall-cell',
  standalone: true,
  imports: [TooltipComponent],
  template: `
    @if (overallStatus() === 'NONE') {
      <span class="text-xs text-zinc-400">—</span>
    } @else {
      <span
        #badgeEl
        class="inline-flex cursor-default items-center rounded px-1.5 py-0.5 text-xs font-semibold"
        [class]="badgeClass()">
        {{ badgeLabel() }}
      </span>
      <app-tooltip
        [target]="badgeEl"
        [title]="tooltipTitle()"
        [content]="tooltipHtml()"
        [position]="'bottom'"
        [minWidth]="220"
        [maxWidth]="360" />
    }
  `,
  styles: ``
})
export class DeployStatusOverallCellComponent {
  value = input<unknown>(null);

  private readonly translate = inject(TranslateService);

  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.currentLang ?? 'en' }
  );

  private readonly entries = computed<DeployStatusEntry[]>(() => {
    const v = this.value();
    const statuses = Array.isArray(v) ? v : (v as DeployStatusCellValue)?.statuses;
    if (!Array.isArray(statuses) || statuses.length === 0) return [];
    return statuses as DeployStatusEntry[];
  });

  private readonly expectedChannels = computed<number | undefined>(() => {
    const v = this.value();
    return Array.isArray(v) ? undefined : (v as DeployStatusCellValue)?.expectedChannels;
  });

  protected readonly overallStatus = computed(() =>
    computeOverallDeployStatus(this.entries(), this.expectedChannels())
  );

  /**
   * The badge reports the deployment rather than the job: every channel out is
   * "Deployed" (purple, the colour a deployed plan wears everywhere else), anything
   * short of that with no failure is "Deploying", and one failed channel is an error
   * however many others went out.
   */
  protected readonly badgeClass = computed(() => {
    switch (this.overallStatus()) {
      case 'SUCCESS':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300';
      case 'ERROR':
        return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
      case 'PROCESSING':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300';
      default:
        return '';
    }
  });

  protected readonly badgeLabel = computed(() => {
    this.currentLang();
    switch (this.overallStatus()) {
      case 'SUCCESS':
        return this.translate.instant('features.plan.deploy.status.deployed');
      case 'ERROR':
        return this.translate.instant('features.plan.deploy.status.error');
      case 'PROCESSING':
        return this.translate.instant('features.plan.deploy.status.deploying');
      default:
        return '';
    }
  });

  protected readonly tooltipTitle = computed(() => {
    this.currentLang();
    return this.translate.instant('features.plan.deploy.status_detail');
  });

  protected readonly tooltipHtml = computed<string>(() =>
    buildDeployTooltipHtml(this.entries(), this.currentLang() ?? 'en')
  );
}
